import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { AttachmentModal } from '../../components/shared/AttachmentComponents';
import {
  categoryColor, documentAttachment, documentKind, groupByCategory, useDocumentCategories,
} from '../../components/workspace/documents/documentHelpers';

function formatEffective(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function PolicyRow({ policy, color, isOpen, onToggle }) {
  const effective = formatEffective(policy.effective_from);
  return (
    <div style={{
      borderRadius: 10,
      border: '1px solid var(--border)',
      background: 'var(--bg-card)',
      marginBottom: 6,
      overflow: 'hidden',
      transition: 'border-color 0.15s',
      borderLeft: `3px solid ${color}`,
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: 14, padding: '13px 16px',
          background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left', color: 'var(--text-primary)',
        }}
      >
        {/* Icon */}
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
            <path d="M4.5 2.5h5.5L13 5v9.5H4.5v-12z" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M9.5 2.5V5.5H13" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M6.5 8h4M6.5 10h4M6.5 12h2.5" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
          </svg>
        </div>

        {/* Title + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 5 }}>
            {policy.title}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 20,
              background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontWeight: 600,
            }}>
              {documentKind(policy)}
            </span>
            {policy.version && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                background: `${color}18`, color: color, letterSpacing: '0.02em',
              }}>
                v{policy.version}
              </span>
            )}
            {effective && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 20,
                background: 'var(--bg-muted)', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                  <rect x="1" y="1.5" width="8" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1" />
                  <path d="M3 1v1M7 1v1M1 4h8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                </svg>
                {effective}
              </span>
            )}
          </div>
        </div>

        {/* Chevron */}
        <svg
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          style={{
            flexShrink: 0, color: 'var(--text-muted)',
            transition: 'transform 0.2s',
            transform: policy.doc_type === 'file' ? 'rotate(-90deg)' : isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && policy.doc_type !== 'file' && (
        <div style={{
          borderTop: `1px solid var(--border)`,
          background: 'var(--bg-muted)',
        }}>
          {policy.body_md ? (
            <div
              className="rich-post-content"
              style={{ margin: 0, padding: '18px 20px', maxHeight: 420, overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: policy.body_md }}
            />
          ) : (
            <p style={{ margin: 0, padding: '18px 20px', fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No content available.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyHRPolicies() {
  const { appId } = useParams();
  const api = useApi();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch]     = useState('');
  const [viewDoc, setViewDoc]   = useState(null);
  const { categories } = useDocumentCategories(appId);

  useEffect(() => {
    api.policies.list(appId, { status: 'active', limit: 200 })
      .then(data => {
        setPolicies(Array.isArray(data) ? data : (data.items || []));
        setLoading(false);
      })
      .catch(() => { setError('Failed to load documents'); setLoading(false); });
  }, [appId, api]);

  if (loading) return <p className="muted" style={{ padding: 24 }}>Loading documents…</p>;
  if (error)   return <div className="banner banner-error" style={{ margin: 24 }}>{error}</div>;

  const q = search.trim().toLowerCase();
  const catName = (p) => categories.find(c => String(c.id) === String(p.category_id))?.name || '';
  const filtered = q
    ? policies.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        catName(p).toLowerCase().includes(q)
      )
    : policies;

  const groups = groupByCategory(filtered, categories);

  return (
    <div style={{ padding: '4px 0' }}>

      {/* ── Page header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: 16, marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
            Documents
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {policies.length} {policies.length === 1 ? 'document' : 'documents'}
            {groups.length > 1 && ` · ${groups.length} categories`}
          </p>
        </div>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
            style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>
            <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
            <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              paddingLeft: 32, paddingRight: 14, height: 36,
              fontSize: 13, borderRadius: 8, width: 220,
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* ── Empty state ── */}
      {!filtered.length && (
        <div style={{
          padding: '56px 24px', textAlign: 'center',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, color: 'var(--text-muted)',
        }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12, opacity: 0.4 }}>
            <rect x="8" y="5" width="24" height="30" rx="3" stroke="currentColor" strokeWidth="1.5" />
            <path d="M14 14h12M14 19h12M14 24h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <div style={{ fontSize: 14 }}>
            {q ? `No documents matching "${search}"` : 'No documents yet.'}
          </div>
        </div>
      )}

      {/* ── Grouped list ── */}
      {groups.map(g => {
        const color = categoryColor(g.name);
        return (
          <div key={g.id} style={{ marginBottom: 28 }}>
            {/* Category header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: color, flexShrink: 0,
              }} />
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                textTransform: 'uppercase', color: 'var(--text-muted)',
              }}>
                {g.name}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '1px 7px',
                borderRadius: 20, background: `${color}18`, color: color,
              }}>
                {g.docs.length}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>

            {/* Policy rows */}
            {g.docs.map(policy => (
              <PolicyRow
                key={policy.id}
                policy={policy}
                color={color}
                isOpen={expanded === policy.id}
                onToggle={() => (policy.doc_type === 'file'
                  ? setViewDoc(policy)
                  : setExpanded(expanded === policy.id ? null : policy.id))}
              />
            ))}
          </div>
        );
      })}

      {viewDoc && <AttachmentModal attachment={documentAttachment(viewDoc)} onClose={() => setViewDoc(null)} />}
    </div>
  );
}
